import Grid from '@material-ui/core/Grid'
import { FormApi, ValidationErrors } from 'final-form'
import { FC, useRef, useState } from 'react'
import { Form as FinalForm } from 'react-final-form'
import Button from '../../../control/button/button'
import Dialog from '../../../control/dialog/dialog'
import MyTextfield from '../../../control/textfield/textfield'
import useTranslate from '../../../i18n/useTranslate'
import { useParams } from 'react-router-dom'
import useInfoChangePassword from '../../../services/um/useInfoChangePassword'
import { IconButton } from '@material-ui/core'
import Visibility from '@material-ui/icons/Visibility'
import VisibilityOff from '@material-ui/icons/VisibilityOff'

type FormValue = {
    newPassword: string | null
    confirmNewPassword: string | null
}

const DialogSetPassword: FC<{}> = () => {
    const translate = useTranslate()
    const setPasswordDialogRef = useRef<Record<string, Function>>()
    const successRef = useRef<Record<string, Function>>()
    const failRef = useRef<Record<string, Function>>()
    const params = useParams<{ id: string }>()
    const { changeUserPasswordById } = useInfoChangePassword()

    const [displayPW, setDisplayPW] = useState({
        password: false,
        confirmPassword: false,
    })

    const initialValue = {
        password: null,
        confirmPassword: null,
    }

    const clearSetPasswordValues = (form: FormApi<FormValue, Partial<FormValue>>) => {
        /** Clear password, confirmPassword */
        form.reset()
    }

    const submitSetPassword = async(formVal: FormValue) => {
        /** Call Service Set Password */
        const result = await changeUserPasswordById({
            userId: Number(params.id),
            newPassword: `${formVal.newPassword}`,
        })
        if (result.message === 'success') {
            closeDialog()
            successRef.current?.openDialog()
        } else {
            failRef.current?.openDialog()
        }
    }

    const validateForm = (values: FormValue) => {
        const errors: ValidationErrors = {}

        if (values.newPassword && values.confirmNewPassword) {
            if (values.confirmNewPassword !== values.newPassword) {
                errors.confirmNewPassword = translate.get('MESSAGE.M019')
            }
        }

        return errors
    }

    const openSetPasswordDialog = () => {
        setPasswordDialogRef?.current?.openDialog()
    }

    const onSetPasswordDialogRef = (func: Record<string, Function>) => {
        setPasswordDialogRef.current = { ...func }
    }

    const onSetSuccessRef = (func: Record<string, Function>) => {
        successRef.current = { ...func }
    }

    const onSetFailRef = (func: Record<string, Function>) => {
        failRef.current = { ...func }
    }

    const closeDialog = () => {
        setPasswordDialogRef?.current?.closeDialog()
    }
    
    const handleClickDisplayPassword = () => {
        setDisplayPW((prev) => ({ ...prev, password: !prev.password }))
    }

    const handleClickDisplayConfirmPassword = () => {
        setDisplayPW((prev) => ({ ...prev, confirmPassword: !prev.confirmPassword }))
    }

    return (
        <FinalForm
            onSubmit={submitSetPassword}
            initialValues={initialValue}
            validate={validateForm}
        >
            {({ handleSubmit, form }) => {
                return (
                    <>
                        <Grid item>
                            <Button
                                size={'large'}
                                variant="contained"
                                color="primary"
                                style={{ minWidth: '0px' }}
                                onClick={openSetPasswordDialog}
                            >
                                {translate.get('BUTTON.SET_NEW_PASSWORD')}
                            </Button>
                        </Grid>
                        <Dialog
                            onRef={onSetPasswordDialogRef}
                            type="dialog-element"
                            title={translate.get('BUTTON.RESET_PASSWORD')}
                            onAfterClose={() => clearSetPasswordValues(form)}
                            onClickConfirm={handleSubmit}
                            content={
                                <Grid container>
                                    <Grid item xs={12}>
                                        <MyTextfield
                                            name="newPassword"
                                            materialInputProps={{
                                                label: translate.get('UM.PROFILE.NEW_PASSWORD'),
                                                placeholder: translate.get('UM.PROFILE.NEW_PASSWORD'),
                                                size: 'small',
                                            }}
                                            type={displayPW.password ? 'text' : 'password'}
                                            icon={
                                                <IconButton size="small" onClick={handleClickDisplayPassword}>
                                                    {displayPW.password ? (
                                                        <Visibility style={{ fontSize: '1.2rem' }} />
                                                    ) : (
                                                        <VisibilityOff style={{ fontSize: '1.2rem' }} />
                                                    )}
                                                </IconButton>
                                            }
                                            handleValueChange={(val: string) => {
                                                if (val) {
                                                    const txtLogin = val.replace(/[ก-๙]|\s/g, '')
                                                    form.change('newPassword', txtLogin)
                                                }
                                            }}
                                            required
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <MyTextfield
                                            name="confirmNewPassword"
                                            materialInputProps={{
                                                label: translate.get('UM.PROFILE.CONFIRM_NEW_PASSWORD'),
                                                placeholder: translate.get('UM.PROFILE.CONFIRM_NEW_PASSWORD'),
                                                size: 'small',
                                            }}
                                            type={displayPW.confirmPassword ? 'text' : 'password'}
                                            icon={
                                                <IconButton size="small" onClick={handleClickDisplayConfirmPassword}>
                                                    {displayPW.confirmPassword ? (
                                                        <Visibility style={{ fontSize: '1.2rem' }} />
                                                    ) : (
                                                        <VisibilityOff style={{ fontSize: '1.2rem' }} />
                                                    )}
                                                </IconButton>
                                            }
                                            handleValueChange={(val: string) => {
                                                if (val) {
                                                    const txtLogin = val.replace(/[ก-๙]|\s/g, '')
                                                    form.change('confirmNewPassword', txtLogin)
                                                }
                                            }}
                                            required
                                        />
                                    </Grid>
                                </Grid>
                            }
                        />
                        <Dialog
                            onRef={onSetSuccessRef}
                            type="alert-message"
                            title={translate.get('ALERT.TITLE.NOTIFICATION')}
                            onAfterClose={closeDialog}
                            content={translate.get('ALERT.CONTENT.SAVE_SUCCESS')}
                        />
                        <Dialog
                            onRef={onSetFailRef}
                            type="alert-message"
                            title={translate.get('ALERT.TITLE.NOTIFICATION')}
                            content={translate.get('ALERT.CONTENT.SAVE_NOT_SUCCESS')}
                        />
                    </>
                )
            }}
        </FinalForm>
    )
}

export default DialogSetPassword
